/**
 * Stopgap auth bootstrap for the dine-in customer experience.
 *
 * Problem: the live API requires a JWT on EVERY endpoint, including
 * `/restaurant/MenuCategory` and `/restaurant/MenuItem` which the
 * QR-scanning customer needs to browse. Anonymous calls return 401,
 * so the menu screen renders an empty / error state and the customer
 * gives up before placing a single order.
 *
 * Tracked as G39 + §32 — the proper fix is for the backend to expose
 * `GET /restaurant/dine/{qrToken}/menu` (public). Until then, we
 * auto-login the customer's tab as a tenant-scoped guest user whose
 * permissions are limited to menu reads + order placement.
 *
 * Configuration:
 *   `VITE_DINE_GUEST_USER`  — username (e.g. `dine-guest`)
 *   `VITE_DINE_GUEST_PASS`  — password
 *
 * The tenant code is taken from the URL (the `:tenantCode` route
 * param). If either env var is missing this hook does nothing —
 * the menu screen will still show the 401-state error message
 * we surface in `useDineMenu`.
 */
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { authService } from '@kss/api';
// `@kss/auth` is the same package the panel uses — the web app's
// store already wires its `authReducer` so a login() dispatch is
// picked up by apiClient's request interceptor automatically.
import { login, decodeJwt, extractPermissions, type AuthState } from '@kss/auth';

type RootWithAuth = { auth: AuthState };

export function useDineGuestLogin() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((s: RootWithAuth) => s.auth.isAuthenticated);
  const { tenantCode = '' } = useParams<{ tenantCode: string }>();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    if (isAuthenticated) return;
    if (!tenantCode) return;

    const envUser = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_DINE_GUEST_USER;
    const envPass = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_DINE_GUEST_PASS;
    if (!envUser || !envPass) return;

    ran.current = true;
    (async () => {
      try {
        const resp = await authService.login({
          tenantCode,
          userName: envUser,
          password: envPass,
        });
        const claims = decodeJwt(resp.accessToken);
        const role = String(
          claims?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ??
          claims?.role ??
          'guest',
        );
        const permissions = extractPermissions(claims);
        dispatch(
          login({
            user: {
              id: resp.userId,
              name: String(claims?.name ?? resp.userName ?? 'Guest'),
              email: '',
              role,
              permissions,
              tenantId: resp.tenantId,
            },
            session: {
              accessToken: resp.accessToken,
              refreshToken: resp.refreshToken,
              expiresAt: resp.expiresAt,
              tenantId: resp.tenantId,
              tenantCode: resp.tenantCode ?? tenantCode,
              sessionId: resp.sessionId,
            },
          }),
        );
      } catch {
        // Guest user probably not provisioned on this tenant — the
        // menu screen will surface a friendlier "needs setup" error.
      }
    })();
  }, [isAuthenticated, tenantCode, dispatch]);
}
